import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface BacklinksMetrics {
  totalBacklinks: number;
  activeBacklinks: number;
  pendingBacklinks: number;
  failedBacklinks: number;
  last30Days: number;
  loading: boolean;
  error: string | null;
}

export function useBacklinksMetrics(websiteId: string, enabled: boolean = true): BacklinksMetrics {
  const [metrics, setMetrics] = useState<BacklinksMetrics>({
    totalBacklinks: 0,
    activeBacklinks: 0,
    pendingBacklinks: 0,
    failedBacklinks: 0,
    last30Days: 0,
    loading: true,
    error: null
  });

  useEffect(() => {
    if (!websiteId || !enabled) {
      setMetrics(prev => ({ ...prev, loading: false }));
      return;
    }

    const fetchBacklinksMetrics = async () => {
      try {
        setMetrics(prev => ({ ...prev, loading: true, error: null }));

        // Get total backlinks count
        const { count: totalCount, error: totalError } = await supabase
          .from('backlinks')
          .select('*', { count: 'exact', head: true })
          .eq('website_id', websiteId);

        if (totalError) throw totalError;

        // Get backlinks by status
        const { data: statusData, error: statusError } = await supabase
          .from('backlinks')
          .select('status')
          .eq('website_id', websiteId);

        if (statusError) throw statusError;

        // Get backlinks from last 30 days
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        const { count: last30DaysCount, error: last30DaysError } = await supabase
          .from('backlinks')
          .select('*', { count: 'exact', head: true })
          .eq('website_id', websiteId)
          .gte('placed_at', thirtyDaysAgo.toISOString());

        if (last30DaysError) throw last30DaysError;

        // Calculate status counts
        const statusCounts = statusData?.reduce((acc, backlink) => {
          acc[backlink.status] = (acc[backlink.status] || 0) + 1;
          return acc;
        }, {} as Record<string, number>) || {};

        setMetrics({
          totalBacklinks: totalCount || 0,
          activeBacklinks: statusCounts.active || 0,
          pendingBacklinks: statusCounts.pending || 0,
          failedBacklinks: statusCounts.failed || 0,
          last30Days: last30DaysCount || 0,
          loading: false,
          error: null
        });
      } catch (error) {
        console.error('Error fetching backlinks metrics:', error);
        setMetrics(prev => ({
          ...prev,
          loading: false,
          error: error instanceof Error ? error.message : 'Failed to fetch backlinks metrics'
        }));
      }
    };

    fetchBacklinksMetrics();
  }, [websiteId, enabled]);

  return metrics;
}


